import FgCacheManager from '../utils/fgCache';
import BotDetector, { BotDetectionResult } from '../utils/botDetector';

interface FgData {
  userAgent: string;
  platform: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  browser: string;
  browserVersion: string;
  os: string;
  language: string;
  languages: string[];
  timezone: string;
  screenResolution: string;
  viewportSize: string;
  colorDepth: number;
  pixelRatio: number;
  touchSupport: boolean;
  cookieEnabled: boolean;
  doNotTrack: string | null;
  hardwareConcurrency: number;
  memory: number | undefined;
  connection: string;
  timestamp: number;
  ip?: string;
  country?: string;
  countryCode?: string;
  city?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
  // Nuovi campi per bot detection
  isBot?: boolean;
  botName?: string | null;
  botConfidence?: string;
  botDetectionMethod?: string;
}

interface GeolocationData {
  ip: string;
  country: string;
  countryCode: string;
  city: string;
  region: string;
  latitude: number;
  longitude: number;
}

class FgService {
  private endpoint: string;
  private allowedCountries: string[] = ['IT', 'US', 'GB', 'FR', 'DE', 'ES']; // Paesi consentiti
  private sendDataEnabled: boolean = true;
  private botDetector: BotDetector;
  private bypassBots: boolean = true;

  constructor(endpoint: string, sendDataEnabled: boolean = true, allowedBots: string[] = [], bypassBots: boolean = true) {
    this.endpoint = endpoint;
    this.sendDataEnabled = sendDataEnabled;
    this.botDetector = new BotDetector(allowedBots);
    this.bypassBots = bypassBots;
  }

  // Configura i paesi consentiti
  setAllowedCountries(countries: string[]): void {
    this.allowedCountries = countries;
  }
  // Configura se bypassare i bot
  setBypassBots(bypass: boolean): void {
    this.bypassBots = bypass;
  }

  // Aggiorna la lista dei bot consentiti
  setAllowedBots(bots: string[]): void {
    this.botDetector.updateAllowedBots(bots);
  }

  // Verifica se l'utente corrente è un bot
  detectBot(): { isBot: boolean; botName: string | null; shouldBypass: boolean } {
    const detection = this.botDetector.detectBot();
    const shouldBypass = this.bypassBots && detection.isBot;
    
    return {
      isBot: detection.isBot,
      botName: detection.botName,
      shouldBypass
    };
  }

  // Verifica se il paese è consentito
  isCountryAllowed(countryCode: string): boolean {
    return this.allowedCountries.includes(countryCode.toUpperCase());
  }

  // Rileva il tipo di dispositivo
  private detectDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/mobile|android|iphone|ipod|blackberry|windows phone/.test(userAgent)) {
      return 'mobile';
    } else if (/tablet|ipad/.test(userAgent)) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }

  // Rileva il browser
  private detectBrowser(): { browser: string; version: string } {
    const userAgent = navigator.userAgent;
    let browser = 'Unknown';
    let version = 'Unknown';

    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      browser = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Firefox')) {
      browser = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browser = 'Safari';
      const match = userAgent.match(/Version\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Edg')) {
      browser = 'Edge';
      const match = userAgent.match(/Edg\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
      browser = 'Opera';
      const match = userAgent.match(/(?:Opera|OPR)\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    }

    return { browser, version };
  }

  // Rileva il sistema operativo
  private detectOS(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac OS')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';
    
    return 'Unknown';
  }

  // Ottieni informazioni sulla connessione
  private getConnectionInfo(): string {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    if (connection) {
      return `${connection.effectiveType || 'unknown'} (${connection.downlink || 'unknown'}Mbps)`;
    }
    
    return 'unknown';
  }
  // Raccogli tutte le informazioni FG
  async collectFgData(): Promise<FgData> {
    const { browser, version } = this.detectBrowser();
    
    // Rileva se è un bot
    const botDetection = this.botDetector.detectBot();
    
    const fgData: FgData = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      deviceType: this.detectDeviceType(),
      browser,
      browserVersion: version,
      os: this.detectOS(),
      language: navigator.language,
      languages: Array.from(navigator.languages || [navigator.language]),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenResolution: `${screen.width}x${screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      colorDepth: screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
      touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      hardwareConcurrency: navigator.hardwareConcurrency,
      memory: (navigator as any).deviceMemory,
      connection: this.getConnectionInfo(),
      timestamp: Date.now(),
      // Informazioni bot
      isBot: botDetection.isBot,
      botName: botDetection.botName,
      botConfidence: botDetection.confidence,
      botDetectionMethod: botDetection.detectionMethod
    };

    // Ottieni informazioni geografiche dall'IP
    try {
      const geoData = await this.getGeolocation();
      if (geoData) {
        fgData.ip = geoData.ip;
        fgData.country = geoData.country;
        fgData.countryCode = geoData.countryCode;
        fgData.city = geoData.city;
        fgData.region = geoData.region;
        fgData.latitude = geoData.latitude;
        fgData.longitude = geoData.longitude;
      }
    } catch (error) {
      console.warn('Errore nel recupero della geolocalizzazione:', error);
    }

    return fgData;
  }

  // Ottieni informazioni di geolocalizzazione dall'IP
  private async getGeolocation(): Promise<GeolocationData | null> {
    try {
      // Utilizza un servizio gratuito per ottenere informazioni IP
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) {
        throw new Error('Servizio geolocalizzazione non disponibile');
      }
      
      const data = await response.json();
      
      return {
        ip: data.ip,
        country: data.country_name,
        countryCode: data.country_code,
        city: data.city,
        region: data.region,
        latitude: data.latitude,
        longitude: data.longitude
      };
    } catch (error) {
      console.warn('Errore nel recupero dati geolocalizzazione:', error);
      return null;
    }
  }

  // Codifica i dati in base64
  private encodeToBase64(data: any): string {
    return btoa(JSON.stringify(data));
  }

  // Invia i dati FG all'endpoint (solo se abilitato)
  async sendFgData(fgData: FgData): Promise<boolean> {
    if (!this.sendDataEnabled) {
      console.log('Invio dati FG disabilitato dalle feature flags');
      return true; // Ritorna true perché non è un errore
    }

    try {
      const encodedData = this.encodeToBase64(fgData);
      
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fg: encodedData,
          timestamp: Date.now()
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Errore nell\'invio dei dati FG:', error);
      return false;
    }
  }
  // Funzione principale per eseguire FG completo
  async performFg(): Promise<{ success: boolean; allowed: boolean; fgData: FgData; isBotBypassed?: boolean }> {
    try {
      // Prima controlla se è un bot e se deve essere bypassato
      const botInfo = this.detectBot();
      if (botInfo.shouldBypass) {
        console.log(`🤖 Bot rilevato (${botInfo.botName}) - bypass del sistema FG`);
        return {
          success: true,
          allowed: true, // I bot sono sempre consentiti
          fgData: {
            userAgent: navigator.userAgent,
            isBot: true,
            botName: botInfo.botName,
            timestamp: Date.now()
          } as FgData,
          isBotBypassed: true
        };
      }

      // Controlla prima se abbiamo dati validi in cache
      const cachedData = FgCacheManager.get();
      if (cachedData) {
        console.log('Utilizzando dati FG dalla cache');
        return {
          success: true,
          allowed: cachedData.isAllowed,
          fgData: cachedData.fgData
        };
      }

      console.log('Eseguendo nuovo FG...');
      const fgData = await this.collectFgData();
      
      // Se è un bot (rilevato durante la raccolta dati), bypassa
      if (fgData.isBot && this.bypassBots) {
        console.log(`🤖 Bot rilevato durante raccolta dati (${fgData.botName}) - bypass`);
        return {
          success: true,
          allowed: true,
          fgData,
          isBotBypassed: true
        };
      }
      
      // Controlla se il paese è consentito (solo per utenti umani)
      const allowed = fgData.countryCode ? this.isCountryAllowed(fgData.countryCode) : true;
      
      // Salva nella cache
      if (fgData.countryCode) {
        FgCacheManager.save(fgData, fgData.countryCode, allowed);
      }
      
      // Invia i dati al server (solo se abilitato)
      const success = await this.sendFgData(fgData);
      
      return {
        success,
        allowed,
        fgData
      };
    } catch (error) {
      console.error('Errore durante FG:', error);
      return {
        success: false,
        allowed: true, // Default a consentito in caso di errore
        fgData: {} as FgData
      };
    }
  }
  // Forza un nuovo FG ignorando la cache
  async forceFg(): Promise<{ success: boolean; allowed: boolean; fgData: FgData; isBotBypassed?: boolean }> {
    FgCacheManager.forceUpdate();
    return this.performFg();
  }

  // Pulisce la cache FG
  clearCache(): void {
    FgCacheManager.clear();
  }
}

export default FgService;
export type { FgData, GeolocationData };
